import React from "react";
import "./css/purchase_analysis_by_term.scss";
import { useDocument } from "../../loan_tools/loan_tool";
import { useUser } from "../../../hooks/user";
import { loan_visible_for_viewer } from "../../../constants/lender_visibility";
import DateField, { today } from "../../fields/date_field";
import CurrencyField from "../../fields/currency_field";
import PercentField from "../../fields/percent_field";
import PhoneField from "../../fields/phone_field";
import Image from "../../ui/image";
import { borrower_names } from "../../../constants/utils";
import {
  loan_amount_first_mortgage,
  loan_term_in_years,
  total_closing_costs,
  first_mortgage_payment_p_and_i
} from "../../../calculations/loan";
import Decimal from "decimal.js";

const DEFAULT_YEARS = [3, 5, 7, 10];
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

export default function PurchaseAnalysisByTerm({ yearCols = DEFAULT_YEARS, setYearCols = () => {} }) {
  const [{ companyProfile, accountProfile }] = useUser();
  const [
    { loans, property, property_to_be_sold, is_selling_property, applicant },
    ,
    { documentType, documentAccountProfile }
  ] = useDocument();

  const borrowers = (applicant && applicant.borrowers) || [];
  const firstBorrower = borrowers[0] || {};
  const borrowerNames = borrower_names(borrowers);
  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean)
    .join(" ");

  function setYear(colIdx, val) {
    const n = parseInt(val, 10);
    if (!n || n < 1 || n > 30) return;
    const updated = [...yearCols];
    updated[colIdx] = n;
    setYearCols(updated);
  }

  return (
    <div className="purchase-analysis-by-term">

      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div className="pa-header">

        {/* Company logo — pulled from Button Admin Panel */}
        <div className="pa-logo">
          <Image className="company-logo" url="company/company-logo" />
        </div>

        {/* Borrower(s) — up to 4, pulled from Applicant page */}
        <div className="pa-borrower-info">
          <div className="pa-section-label">CLIENT(S)</div>
          <div className="pa-borrower-grid">
            {/* Column 1: borrowers 0 and 1 */}
            <div className="pa-borrower-col">
              {borrowerNames[0] && <div className="pa-name">{borrowerNames[0]}</div>}
              {borrowerNames[1] && <div className="pa-name">{borrowerNames[1]}</div>}
            </div>
            {/* Column 2: borrowers 2 and 3 (co-borrowers) */}
            {(borrowerNames[2] || borrowerNames[3]) && (
              <div className="pa-borrower-col">
                {borrowerNames[2] && <div className="pa-name">{borrowerNames[2]}</div>}
                {borrowerNames[3] && <div className="pa-name">{borrowerNames[3]}</div>}
              </div>
            )}
          </div>
          {firstBorrower.current_address && (
            <div className="pa-addr">{firstBorrower.current_address}</div>
          )}
          {firstBorrower.current_address_2 && (
            <div className="pa-addr">{firstBorrower.current_address_2}</div>
          )}
          <div className="pa-addr">
            {[firstBorrower.city, firstBorrower.state].filter(Boolean).join(", ")}
            {firstBorrower.zip_code ? " " + firstBorrower.zip_code : ""}
          </div>
        </div>

        {/* Loan Officer info + Dates — pulled from User Management */}
        <div className="pa-lo-dates">
          <div className="pa-lo-info">
            {loName && <div className="pa-lo-name">{loName}</div>}
            {documentAccountProfile.nmls_number && (
              <div>NMLS #: {documentAccountProfile.nmls_number}</div>
            )}
            {documentAccountProfile.state_real_estate_license && (
              <div>Lic #: {documentAccountProfile.state_real_estate_license}</div>
            )}
            {documentAccountProfile.direct_contact_phone && (
              <div className="pa-phone-row">
                Cell:{" "}
                <PhoneField naked value={documentAccountProfile.direct_contact_phone} />
              </div>
            )}
            {documentAccountProfile.email && (
              <div>{documentAccountProfile.email}</div>
            )}
          </div>
          <div className="pa-dates">
            <div>
              <span className="pa-date-label">DATE FIRST ISSUED:</span>
              <DateField naked value={applicant && applicant.first_contact_date} />
            </div>
            <div>
              <span className="pa-date-label">DATE LAST ISSUED:</span>
              <DateField naked value={today()} />
            </div>
          </div>
        </div>

      </div>

      {/* ── Title Bar ───────────────────────────────────────────────── */}
      <div className="pa-title-block">
        <div className="pa-title">Purchase Loan ROI Analysis</div>
        <div className="pa-subtitle">
          Loan Scenarios on Years for Return on Investment
        </div>
      </div>

      {/* ── Analysis Table ──────────────────────────────────────────── */}
      <table className="pa-table">
        <thead>
          <tr className="pa-col-headers">
            <th className="pa-th-loan">Loan</th>
            <th>Loan Amount</th>
            <th>Interest Rate</th>
            <th>Annual P&amp;I Payment</th>
            <th>Term</th>
            {yearCols.map((yr, i) => (
              <th key={i} className="pa-year-th">
                <select
                  className="pa-year-select"
                  value={yr}
                  onChange={e => setYear(i, e.target.value)}
                >
                  {YEAR_OPTIONS.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <div className="pa-year-label">Year{yr !== 1 ? "s" : ""}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(loans || []).map((loan, loanIdx) => {
            if (!loan_visible_for_viewer(loan, accountProfile)) {
              return null;
            }
            const loanAmount = loan_amount_first_mortgage(
              documentType, loan, property, property_to_be_sold, is_selling_property
            );
            const monthlyPI = first_mortgage_payment_p_and_i(
              documentType, loan, property, property_to_be_sold, is_selling_property
            );
            const annualPI = monthlyPI.mul(12);
            const closingCosts = total_closing_costs(
              documentType, loan, property, property_to_be_sold, is_selling_property
            );
            const termYears = loan_term_in_years(loan);

            return (
              <React.Fragment key={loanIdx}>
                <tr className="pa-data-row">
                  <td className="pa-loan-num">Loan {loanIdx + 1}</td>
                  <td className="pa-right">
                    <CurrencyField naked fixedDigits={0} value={loanAmount} />
                  </td>
                  <td className="pa-center">
                    <PercentField naked decimalPlaces={3} value={loan.rate} />
                  </td>
                  <td className="pa-right">
                    <CurrencyField naked fixedDigits={0} value={annualPI} />
                  </td>
                  <td className="pa-center">{termYears.toString()} Yrs</td>
                  {yearCols.map((yr, i) => <td key={i} />)}
                </tr>

                <tr className="pa-monthly-row">
                  <td colSpan={5} className="pa-row-label">
                    Monthly P&amp;I Payment
                  </td>
                  {yearCols.map((yr, i) => (
                    <td key={i} className="pa-right">
                      <CurrencyField naked fixedDigits={0} value={monthlyPI} />
                    </td>
                  ))}
                </tr>

                <tr className="pa-cost-row">
                  <td colSpan={5} className="pa-row-label">
                    Cost of Loan Over Time (Principal and Interest Payment)
                  </td>
                  {yearCols.map((yr, i) => {
                    const cappedYears = Decimal.min(new Decimal(yr), termYears);
                    return (
                      <td key={i} className="pa-right">
                        <CurrencyField naked fixedDigits={0} value={monthlyPI.mul(cappedYears).mul(12)} />
                      </td>
                    );
                  })}
                </tr>

                <tr className="pa-closing-row">
                  <td colSpan={5} className="pa-row-label">
                    Section J Closing Costs from Loan Estimate
                  </td>
                  {yearCols.map((yr, i) => (
                    <td key={i} className="pa-right">
                      <CurrencyField naked fixedDigits={0} value={closingCosts} />
                    </td>
                  ))}
                </tr>

                <tr className="pa-total-row">
                  <td colSpan={5} className="pa-row-label pa-bold">
                    Total Cost of Purchasing the Home Over Time
                  </td>
                  {yearCols.map((yr, i) => {
                    const cappedYears = Decimal.min(new Decimal(yr), termYears);
                    return (
                      <td key={i} className="pa-right pa-bold">
                        <CurrencyField naked fixedDigits={0} value={monthlyPI.mul(cappedYears).mul(12).add(closingCosts)} />
                      </td>
                    );
                  })}
                </tr>

                <tr className="pa-spacer">
                  <td colSpan={5 + yearCols.length} />
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="pa-footer">
        &#169; 2016 The LANIS Group, LLC. &nbsp; This analysis is for informational purposes only.
      </div>

    </div>
  );
}
